import type {
    DataConnection,
    DataType,
    FunctionDefinition,
    FunctionPortDefinition,
    NodeInstance,
    NodeInstanceState,
} from '@glueapp/graph-types';
import { createSingleNodeInstance } from '@glueapp/graph-nodes';

export type AuthMethod = 'basic' | 'bearer' | 'apiKey' | 'custom';

const FUNCTION_INPUTS_NODE_ID = 1;
const FUNCTION_OUTPUTS_NODE_ID = 2;
const AUTH_NODE_ID = 3;
const CHECK_PASSWORD_NODE_ID = 4;
const CENTER = 20000; // half of DRAW_CONFIG.BASE_SIZE (40000)
const NODE_SPACING = 500;

const headersListType: DataType = {
    kind: 'array',
    subType: {
        kind: 'object',
        properties: { name: { kind: 'string' }, value: { kind: 'string' } },
        restriction: 'exact',
    },
};

interface ExtraNode {
    nodeId: number;
    nodeType: string;
    position: { x: number; y: number };
    state?: NodeInstanceState;
}

interface AuthMethodConfig {
    name: string;
    inputPortDefinitions: FunctionPortDefinition[];
    outputPortDefinitions: FunctionPortDefinition[];
    authNodeId: string | null; // null for custom (no pre-created node)
    extraNodes: ExtraNode[];
    connections: DataConnection[];
}

function makePort(id: string, name: string, type: DataType): FunctionPortDefinition {
    return { id, name, type };
}

function makeConnection(
    connectionId: number,
    sendingNodeId: number,
    sendingPortName: string,
    receivingNodeId: number,
    receivingPortName: string,
): DataConnection {
    return {
        id: connectionId,
        sendingNodeInstanceId: sendingNodeId,
        sendingPortName,
        sendingPortType: 'external',
        receivingNodeInstanceId: receivingNodeId,
        receivingPortName,
        receivingPortType: 'external',
    };
}

function getMethodConfig(method: AuthMethod): AuthMethodConfig {
    switch (method) {
        case 'basic':
            return {
                name: 'Basic Auth',
                inputPortDefinitions: [makePort('auth', 'Authorization Header', { kind: 'string' })],
                outputPortDefinitions: [
                    makePort('authenticated', 'Authenticated', { kind: 'boolean' }),
                    makePort('username', 'Username', { kind: 'string' }),
                ],
                authNodeId: 'ParseBasicAuth',
                extraNodes: [
                    {
                        nodeId: CHECK_PASSWORD_NODE_ID,
                        nodeType: 'CheckPassword',
                        position: { x: CENTER + NODE_SPACING / 3, y: CENTER },
                        state: { algorithm: 'argon2' },
                    },
                ],
                connections: [
                    // FunctionInputs.Authorization Header → ParseBasicAuth.Authorization
                    makeConnection(1, FUNCTION_INPUTS_NODE_ID, 'Authorization Header', AUTH_NODE_ID, 'Authorization'),
                    // ParseBasicAuth.Password → CheckPassword.Password
                    makeConnection(2, AUTH_NODE_ID, 'Password', CHECK_PASSWORD_NODE_ID, 'Password'),
                    // CheckPassword.Expected Hash is intentionally left unconnected —
                    // the user wires their own data source (e.g., database lookup) to it
                    // CheckPassword.Valid → FunctionOutputs.Authenticated
                    makeConnection(3, CHECK_PASSWORD_NODE_ID, 'Valid', FUNCTION_OUTPUTS_NODE_ID, 'Authenticated'),
                    // ParseBasicAuth.Username → FunctionOutputs.Username
                    makeConnection(4, AUTH_NODE_ID, 'Username', FUNCTION_OUTPUTS_NODE_ID, 'Username'),
                ],
            };

        case 'bearer':
            return {
                name: 'Bearer Token Auth',
                inputPortDefinitions: [makePort('auth', 'Authorization Header', { kind: 'string' })],
                outputPortDefinitions: [
                    makePort('authenticated', 'Authenticated', { kind: 'boolean' }),
                    makePort('token', 'Token', { kind: 'string' }),
                ],
                authNodeId: 'ParseBearerToken',
                extraNodes: [],
                connections: [
                    makeConnection(1, FUNCTION_INPUTS_NODE_ID, 'Authorization Header', AUTH_NODE_ID, 'Authorization'),
                    // Token is extracted and passed through
                    makeConnection(2, AUTH_NODE_ID, 'Token', FUNCTION_OUTPUTS_NODE_ID, 'Token'),
                    // Authenticated is intentionally left unconnected —
                    // the user wires their own token validation logic (e.g., JWT verify, database lookup)
                ],
            };

        case 'apiKey':
            return {
                name: 'API Key Auth',
                inputPortDefinitions: [
                    makePort('headers', 'Headers', headersListType),
                    makePort('headerName', 'Header Name', { kind: 'string' }),
                ],
                outputPortDefinitions: [
                    makePort('authenticated', 'Authenticated', { kind: 'boolean' }),
                    makePort('value', 'Value', { kind: 'string' }),
                ],
                authNodeId: 'CheckApiKey',
                extraNodes: [],
                connections: [
                    makeConnection(1, FUNCTION_INPUTS_NODE_ID, 'Headers', AUTH_NODE_ID, 'Headers'),
                    makeConnection(2, FUNCTION_INPUTS_NODE_ID, 'Header Name', AUTH_NODE_ID, 'Header Name'),
                    makeConnection(3, AUTH_NODE_ID, 'Valid', FUNCTION_OUTPUTS_NODE_ID, 'Authenticated'),
                    makeConnection(4, AUTH_NODE_ID, 'Value', FUNCTION_OUTPUTS_NODE_ID, 'Value'),
                ],
            };

        case 'custom': {
            const methodUnion: DataType = {
                kind: 'union',
                subTypes: [
                    { kind: 'stringLiteral', stringLiteral: 'GET' },
                    { kind: 'stringLiteral', stringLiteral: 'POST' },
                    { kind: 'stringLiteral', stringLiteral: 'PUT' },
                    { kind: 'stringLiteral', stringLiteral: 'PATCH' },
                    { kind: 'stringLiteral', stringLiteral: 'DELETE' },
                    { kind: 'stringLiteral', stringLiteral: 'HEAD' },
                    { kind: 'stringLiteral', stringLiteral: 'OPTIONS' },
                ],
            };
            const queryParamsType: DataType = {
                kind: 'object',
                properties: {},
                restriction: 'open',
            };
            return {
                name: 'Custom Auth',
                inputPortDefinitions: [
                    makePort('authorization', 'Authorization Header', { kind: 'string' }),
                    makePort('headers', 'Headers', headersListType),
                    makePort('queryParams', 'Query Params', queryParamsType),
                    makePort('body', 'Body', { kind: 'object', properties: {}, restriction: 'open' }),
                    makePort('method', 'Method', methodUnion),
                    makePort('path', 'Path', { kind: 'string' }),
                ],
                outputPortDefinitions: [makePort('authenticated', 'Authenticated', { kind: 'boolean' })],
                authNodeId: null,
                extraNodes: [],
                connections: [],
            };
        }
    }
}

export function createAuthFunction(method: AuthMethod): FunctionDefinition {
    const functionId = `auth_${Date.now()}`;
    const config = getMethodConfig(method);

    const nodeInstances: NodeInstance[] = [
        // FunctionInputs node
        {
            id: FUNCTION_INPUTS_NODE_ID,
            nodeId: 'function-inputs',
            inPorts: [],
            outPorts: config.inputPortDefinitions.map(def => ({
                name: def.name,
                type: def.type,
                optional: def.optional || false,
            })),
            selected: false,
            portValues: {},
            portUserDefinedTypes: {},
            state: {},
            position: { x: CENTER - NODE_SPACING, y: CENTER },
        },
        // FunctionOutputs node
        {
            id: FUNCTION_OUTPUTS_NODE_ID,
            nodeId: 'function-outputs',
            inPorts: config.outputPortDefinitions.map(def => ({
                name: def.name,
                type: def.type,
                optional: def.optional || false,
            })),
            outPorts: [],
            selected: false,
            portValues: {},
            portUserDefinedTypes: {},
            state: {},
            position: { x: CENTER + NODE_SPACING, y: CENTER },
        },
    ];

    // Add auth-specific parser node (if not custom)
    if (config.authNodeId) {
        const authNode = createSingleNodeInstance(AUTH_NODE_ID, config.authNodeId, {
            x: CENTER - NODE_SPACING / 3,
            y: CENTER,
        });
        nodeInstances.push(authNode);
    }

    // Add extra nodes (e.g., CheckPassword for basic auth)
    for (const extra of config.extraNodes) {
        const extraNode = createSingleNodeInstance(extra.nodeId, extra.nodeType, extra.position);
        if (extra.state) {
            extraNode.state = extra.state;
        }
        nodeInstances.push(extraNode);
    }

    return {
        id: functionId,
        name: config.name,
        description: `Auto-generated ${config.name} function`,
        nodeInstances,
        connections: config.connections,
        inputPortDefinitions: config.inputPortDefinitions,
        outputPortDefinitions: config.outputPortDefinitions,
        functionInputsNodeId: FUNCTION_INPUTS_NODE_ID,
        functionOutputsNodeId: FUNCTION_OUTPUTS_NODE_ID,
        createdAt: new Date(),
        updatedAt: new Date(),
        version: 1,
    };
}
